<template>
	<div class="table-overflow-hide" style="background-color: white" v-if="show">
		<div class="debug-info" v-if="tableDebug">
			<pre>showProp: {{ show }}, dataLength: {{ data?.length || 0 }}, columnsLength: {{ columns?.length || 0 }}</pre>
		</div>
		<div class="directoryTable scrollable-55vh">
			<table class="table" data-cy="report-table" v-if="tableData.length > 0">
				<thead class="sticky-header">
					<tr>
						<Typography
							v-for="col in tableHeaders"
							:key="col.key"
							transform="capitalize"
							:align="'start'"
							vertical-align="center"
							size="0.875rem"
							tag="th"
							weight="600"
							class="header-cell"
							@click="if (col.sortKey) setSort(col.sortKey);"
							data-cy="report-table-header"
							:style="`${col.sortKey ? 'cursor: pointer;' : ''} user-select: none`"
						>
							<span class="d-flex align-center gap-2">
								{{ col.title }}
								<v-icon v-if="col.sortKey === currentSort.key"
									>mdi-arrow-{{ currentSort.asc ? "up" : "down" }}</v-icon
								>
							</span>
						</Typography>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(row, i) in tableData" :key="i">
						<td v-for="col in tableHeaders" :key="col.key">
							<template v-if="col.key === 'translator_language' && row[col.key]">
								<component :is="codeToIcon[row[col.key]] || codeToIcon['en']" class="me-2" style="width:20px;height:20px" />
							</template>
							<template v-else-if="col.key === 'meeting_type' && row[col.key]">
								{{ getMeetingTypeLabel(row[col.key], t) }}
							</template>
							<template v-else-if="!isLink(row[col.key])">
								{{ row[col.key] ?? "" }}
							</template>
							<a v-else :href="row[col.key]" target="_blank">Join</a>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				class="w-100 d-flex align-center"
				style="height: 90%"
				v-if="!data?.length && loading"
			>
				<LoadingState text="Loading Report"></LoadingState>
			</div>
			<div
				class="w-100 d-flex align-center"
				v-if="((!data || !data.length) || (Array.isArray(data) && data.length === 0)) && !loading"
				style="height: calc(100% - 100px); border-radius: 15px"
			>
				<div class="empty-state" style="background: white">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find the requested report.</div>
					<p v-if="['teacher', 'team', 'parent'].includes(type) && !entityName">Please select a {{ type }} from the dropdown above</p>
					<p v-else>Please try again later</p>
				</div>
			</div>
		</div>
	</div>

	<div class="d-flex mt-4 gap-2">
		<RouterLink :to="{ query: { type: undefined } }">
			<v-btn data-cy="report-back-btn" append-icon="mdi-arrow-left" color="primary">Back</v-btn>
		</RouterLink>
		<template v-if="show">
			<v-btn
				v-if="showColumnsButton"
				@click="$emit('open-column-modal')"
				append-icon="mdi-view-column"
				color="primary"
				data-cy="report-columns-button"
			>
				Columns
			</v-btn>
			<v-btn
				@click="printTable(columns, data, name, entityName, type)"
				append-icon="mdi-printer"
				color="primary"
				data-cy="report-print-btn"
			>
				Print
			</v-btn>
			<v-btn
				@click="exportTable(columns, data, name)"
				append-icon="mdi-file-export-outline"
				color="primary"
				>Export</v-btn
			>
		</template>

		<v-btn
			v-if="['teacher', 'team', 'parent'].includes(type)"
			@click="printMultiple"
			append-icon="mdi-printer"
			color="primary"
			:loading="
				state.loading ||
				(type == 'teacher' && teachersS.teachers.loading) ||
				(type == 'team' && teamsS.teams.loading) ||
				(type == 'parent' && parentsS.parents.loading)
			"
		>
			<template #loader v-if="state.loading">
				<div
					style="
						display: flex;
						justify-content: space-between;
						flex-direction: column;
						align-items: center;
						width: 100%;
						height: 100%;
					"
				>
					<div style="flex: 1; display: flex; align-items: center">
						{{ state.fetchCount }}/{{ state.totalCount }}
					</div>
					<v-progress-linear
						stream
						:buffer-value="state.bufferValue"
						color="white"
						style="opacity: 1"
						bg-opacity="1"
					>
					</v-progress-linear>
				</div>
			</template>
			Print All
			<v-tooltip activator="parent" class="custom-tooltip" location="bottom">
				Print Report for all {{ type }}s
			</v-tooltip>
		</v-btn>
	</div>
</template>

<script setup lang="ts">
import LoadingState from "@/components/loadingState.vue";
import Typography from "@/components/typography.vue";
import type { tableColumn } from "@/hooks/useTableSort";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import { computed, ref, watchEffect } from "vue";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { exportTable, printTable, printAllTable, getMeetingTypeLabel } from "./utils";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import { isLink } from "@/utils";
import { reactive } from "vue";
import useTeachers from "@/stores/school/teachers";
import useParents from "@/stores/school/parents";
import useTeams from "@/stores/school/teams";
import { onMounted, onUpdated } from 'vue';
import enFlag from '@/assets/icons/flags/usa.svg';
import esFlag from '@/assets/icons/flags/es.svg';
import frFlag from '@/assets/icons/flags/fr.svg';
import deFlag from '@/assets/icons/flags/de.svg';
import arFlag from '@/assets/icons/flags/ar.svg';
import zhFlag from '@/assets/icons/flags/cn.svg';
import jaFlag from '@/assets/icons/flags/jp.svg';
import bnFlag from '@/assets/icons/flags/bd.svg';
import krFlag from '@/assets/icons/flags/kr.svg';
import urFlag from '@/assets/icons/flags/pk.svg';
import woFlag from '@/assets/icons/flags/sn.svg';
import ruFlag from '@/assets/icons/flags/ru.svg';
import uaFlag from '@/assets/icons/flags/ua.svg';
import uzFlag from '@/assets/icons/flags/uz.svg';
import geFlag from '@/assets/icons/flags/ge.svg';
import hkFlag from '@/assets/icons/flags/hk.svg';
import htFlag from '@/assets/icons/flags/ht.svg';
// Define generic types here
type SK = string;
type T = tableColumn<SK>;
const props = defineProps<{
	// @ts-ignore
	// eslint-disable-next-line
	columns: tableColumn<SK>[] | Readonly<tableColumn<SK>[]>;
	// @ts-ignore
	// eslint-disable-next-line
	data: Record<T["key"] | T["sortKey"], any>[];
	loading: boolean;
	name: string;
	type: string;
	eventId: string;
	show: boolean;
	entityName?: string;
	showColumnsButton?: boolean;
	selectedConferenceDayId?: string;
}>();

const emit = defineEmits<{
	(e: "open-column-modal"): void;
}>();

const teachersS = useTeachers();
const teamsS = useTeams();
const parentsS = useParents();
const { t } = useI18n();

// Add debug helper
const tableDebug = ref(false);

// Debug init - will be visible in dev tools
onMounted(() => {
	console.log("ReportTable mounted with data:", { 
		data: props.data, 
		show: props.show,
		columnsLen: props.columns?.length
	});
	
	// Enable debug with a URL param for testing
	if (window.location.search.includes('debug=true')) {
		tableDebug.value = true;
	}
});

const { currentSort, resetSort, setSort } = useTableSort(props.columns);

const codeToIcon: Record<string, any> = {
    en: enFlag,
    es: esFlag,
    fr: frFlag,
    de: deFlag,
    ar: arFlag,
    zh: zhFlag,
    ja: jaFlag,
    bn: bnFlag,
    ko: krFlag,
    ur: urFlag,
    wo: woFlag,
    ru: ruFlag,
    uk: uaFlag,
    "zh-cn": zhFlag,
    uz: uzFlag,
    ka: geFlag,
    yue: hkFlag,
    ht: htFlag,
};

const tableData = computed(() => {
	if (!Array.isArray(props.data) || props.data.length === 0) {
		console.log("No data available for table");
		return [];
	}
	
	// Ensure we have valid data with the sort key
	try {
		return sort(props.data || [], (i) => {
			// Check if the sort key exists on this item
			if (i && currentSort.value && currentSort.value.key in i) {
				return i[currentSort.value.key];
			}
			return ""; // Fallback for missing sort key
		}, currentSort.value.asc);
	} catch (error) {
		console.error("Error sorting table data:", error);
		return props.data || [];
	}
});
const tableHeaders = computed(() => {
	if (!Array.isArray(props.columns)) {
		console.log("No columns available for table");
		return [];
	}
	return props.columns.filter((col) => col && typeof col === 'object' && col.display);
});

watch(
	() => props.columns,
	() => {
		resetSort(props.columns);
	}
);

// Watch for data changes and log them
watch(
	[() => props.data, () => props.show],
	([newData, newShow]) => {
		console.log("Report data or show changed:", { 
			dataLength: Array.isArray(newData) ? newData.length : 0,
			show: newShow, 
			tableDataLength: tableData.value.length
		});
	}
);

// Use watchEffect to monitor all reactive dependencies
watchEffect(() => {
	const dataPresent = Array.isArray(props.data) && props.data.length > 0;
	const showValue = props.show;
	const tableDataValue = tableData.value.length > 0;
	
	if (showValue && dataPresent && !tableDataValue) {
		console.warn("Potential display issue: show=true, data exists, but tableData is empty");
	}
});

const state = reactive({
	loading: false,
	fetchCount: 0,
	totalCount: 0,
	bufferValue: 0,
});

const printMultiple = async () => {
	state.loading = true;
	state.fetchCount = 0;
	state.totalCount = 0;
	state.bufferValue = 0;
	await printAllTable(
		props.columns,
		props.name,
		props.type as any,
		props.eventId,
		(totalCount: number, inc: boolean) => {
			state.totalCount = totalCount;
			if (inc) {
				state.fetchCount++;
				const increaseBy =
					Math.ceil(((state.fetchCount + 1) * 100) / state.totalCount) -
					state.bufferValue;
				const times = 7;
				const eachTime = increaseBy / times;
				for (let i = 0; i < times; i += 1) {
					setTimeout(() => {
						if (state.bufferValue + eachTime < 100) state.bufferValue += eachTime;
					}, i * 100);
				}
			}
		},
		props.selectedConferenceDayId
	);
	state.loading = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.directoryTable table tbody td:first-child {
	width: unset !important;
}
.directoryTable table tbody td {
	word-break: break-all;
}
</style>